import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useBlocker, useNavigate, useParams } from "react-router";
import { Modal } from "../components/Modal";
import { ServerPicker } from "../components/ServerPicker";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Select,
  Textarea,
  buttonStyles,
  cx,
} from "../components/ui";
import {
  useAnnouncement,
  useCreateAnnouncement,
  useUpdateAnnouncement,
} from "../lib/queries";
import type {
  AnnouncementAudience,
  AnnouncementInput,
  AnnouncementKind,
} from "../lib/types";

/**
 * Writing a notice, with what the customer will read beside it.
 *
 * A route rather than a dialog, for the reason the model editor is one: this is
 * ten fields and a preview, and as a modal the preview was off screen while you
 * typed the text it was previewing. A route also survives a reload and can be
 * pasted into an incident channel — which matters more here than it does for a
 * rate card, because writing an incident notice is something two people often do
 * together at speed.
 *
 * The preview is the point of the layout. This is the widest-reaching action in
 * the console — text on every dashboard screen and every public page, with no
 * review step between an operator and a customer — and the thing that most often
 * goes wrong is not the wording but the *length*: 400 characters reads fine in a
 * textarea and is a wall of text in a 40px bar on a phone.
 */

const TITLE_MAX = 160;
const BODY_MAX = 600;

const KINDS: { value: AnnouncementKind; label: string; hint: string }[] = [
  { value: "info", label: "Info", hint: "News. A new region, a new model, a changed default." },
  {
    value: "maintenance",
    label: "Maintenance",
    hint: "Planned, and known about in advance. Give the window in the body.",
  },
  {
    value: "incident",
    label: "Incident",
    hint: "Happening now and nobody chose it. Reaches everyone — be sure.",
  },
  {
    value: "promo",
    label: "Promotion",
    hint:
      "A time-boxed offer. Renders as a solid bar rather than a tint, so set an end date — a " +
      "flash sale with no end is a permanent loud stripe on every page.",
  },
];

const AUDIENCES: { value: AnnouncementAudience; label: string; hint: string }[] = [
  {
    value: "both",
    label: "Both surfaces",
    hint: "The dashboard and the public site. Right for a maintenance window.",
  },
  {
    value: "dashboard",
    label: "Dashboard only",
    hint: "Customers signed in, plus the login screen. Nothing on the public site.",
  },
  {
    value: "web",
    label: "Public site only",
    hint: "Marketing pages. For something a visitor needs and a customer does not.",
  },
];

/** Common maintenance windows, so the usual case is one click and not two pickers. */
const DURATIONS = [
  { label: "30m", minutes: 30 },
  { label: "1h", minutes: 60 },
  { label: "2h", minutes: 120 },
  { label: "4h", minutes: 240 },
];

function blankInput(): AnnouncementInput {
  return {
    kind: "maintenance",
    title: "",
    body: "",
    linkUrl: null,
    linkLabel: null,
    audience: "both",
    serverId: null,
    endsAt: null,
    published: false,
    dismissible: true,
  };
}

export function AnnouncementFormPage() {
  const params = useParams();
  const navigate = useNavigate();

  const announcementId = params.announcementId;
  const editing = Boolean(announcementId);

  const existing = useAnnouncement(announcementId);
  const create = useCreateAnnouncement();
  const update = useUpdateAnnouncement();

  const [form, setForm] = useState<AnnouncementInput>(blankInput);
  const [baseline, setBaseline] = useState<AnnouncementInput>(blankInput);
  const [error, setError] = useState<string | null>(null);
  const savedRef = useRef(false);

  // Seed once the row arrives, and keep a copy so `dirty` means something.
  useEffect(() => {
    const row = existing.data;
    if (!row) return;
    const next: AnnouncementInput = {
      kind: row.kind,
      title: row.title,
      body: row.body,
      linkUrl: row.linkUrl,
      linkLabel: row.linkLabel,
      audience: row.audience,
      serverId: row.serverId,
      startsAt: row.startsAt,
      endsAt: row.endsAt,
      published: row.published,
      dismissible: row.dismissible,
    };
    setForm(next);
    setBaseline(next);
  }, [existing.data?.id]);

  const dirty = useMemo(
    () => !savedRef.current && JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline],
  );

  /*
   * The hazard a dialog did not have. The nav rail, the back button and a stray
   * link can all leave a half-written incident notice behind — and unlike a rate
   * card, the half-written version is the one somebody is waiting for.
   */
  const blocker = useBlocker(() => dirty && !savedRef.current);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const patch = (next: Partial<AnnouncementInput>) =>
    setForm((current) => ({ ...current, ...next }));

  /*
   * The one rule an operator has to be told before they save rather than after.
   * A server-scoped notice cannot reach the public site — the site has no way to
   * know which server a visitor's apps are on — so the picker disappears
   * entirely when it cannot apply, rather than accepting a value the API will
   * refuse.
   */
  const canScopeToServer = form.audience === "dashboard";

  function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    const action = announcementId
      ? update.mutateAsync({ id: announcementId, input: form })
      : create.mutateAsync(form);

    action
      .then(() => {
        savedRef.current = true;
        navigate("/announcements");
      })
      .catch((cause: Error) => setError(cause.message));
  }

  /** Sets the end from the start plus a preset, so the pair cannot invert. */
  function setDuration(minutes: number) {
    const from = form.startsAt ? new Date(form.startsAt) : new Date();
    patch({
      startsAt: from.toISOString(),
      endsAt: new Date(from.getTime() + minutes * 60_000).toISOString(),
    });
  }

  const saving = create.isPending || update.isPending;

  if (editing && existing.isPending) {
    return <LoadingBlock label="Loading announcement…" />;
  }

  if (editing && existing.error) {
    return (
      <div className="space-y-4">
        <PageHeader title="Announcement" />
        <Alert>{existing.error.message}</Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={editing ? "Edit announcement" : "New announcement"}
        description={
          editing && baseline.published
            ? "This notice is live. Saving changes what customers are reading now."
            : "Nothing is shown to anybody until you publish it."
        }
        action={
          <Link to="/announcements" className={buttonStyles("secondary", "md")}>
            Back to announcements
          </Link>
        }
      />

      {/*
        The preview runs the full width and sticks to the top, and both of those
        are corrections rather than choices.

        It began in a sticky sidebar column, which is where the model editor puts
        its price readout — and that is right for a readout and wrong for this.
        The thing being previewed is a full-bleed bar: in a 22rem column the
        message had no room left after the label and the trailing controls, so
        `flex-1` collapsed to nothing and the preview rendered a bar with no text
        in it. A narrow preview of a wide component does not tell a smaller
        truth, it tells a different one.

        Sticky because the whole point is watching the text while typing it.
      */}
      <div className="sticky top-0 z-20 -mt-2 bg-canvas/95 pt-2 pb-4 backdrop-blur">
        <Card>
          <CardHeader
            title="Preview"
            description="Wording, length and layout as a customer meets it. Colours follow this console's palette."
          />
          <div className="grid gap-5 p-5 pt-0 xl:grid-cols-[minmax(0,1fr)_390px]">
            <div className="min-w-0">
              <p className="micro mb-2">Wide</p>
              <Preview form={form} width="desktop" />
            </div>
            <div>
              <p className="micro mb-2">On a phone</p>
              <Preview form={form} width="mobile" />
            </div>
          </div>
        </Card>
      </div>

      <form onSubmit={submit}>
        <div className="grid items-start gap-6">
          <div className="min-w-0 space-y-4">
            {error && <Alert>{error}</Alert>}

            <Card>
              <CardHeader title="What it says" />
              <div className="space-y-4 p-5 pt-0">
                <Field
                  label="Kind"
                  htmlFor="kind"
                  hint={KINDS.find((k) => k.value === form.kind)?.hint}
                >
                  <Select
                    id="kind"
                    value={form.kind}
                    onChange={(e) => patch({ kind: e.target.value as AnnouncementKind })}
                  >
                    {KINDS.map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field
                  label="Title"
                  htmlFor="title"
                  hint="One line. It is the part people read."
                  action={<Counter used={form.title.length} max={TITLE_MAX} />}
                >
                  <Input
                    id="title"
                    value={form.title}
                    maxLength={TITLE_MAX}
                    onChange={(e) => patch({ title: e.target.value })}
                    placeholder="Scheduled maintenance on 22 August"
                  />
                </Field>

                <Field
                  label="Body"
                  htmlFor="body"
                  hint="Plain text, one paragraph. Say what is affected and when it ends."
                  action={<Counter used={form.body.length} max={BODY_MAX} />}
                >
                  <Textarea
                    id="body"
                    rows={4}
                    maxLength={BODY_MAX}
                    value={form.body}
                    onChange={(e) => patch({ body: e.target.value })}
                    placeholder="Deploys will be paused between 02:00 and 03:00 UTC. Running apps are unaffected."
                  />
                </Field>

                <Field
                  label="Link"
                  htmlFor="linkUrl"
                  hint="Optional. A status page or a docs section — http(s) only."
                >
                  <Input
                    id="linkUrl"
                    type="url"
                    value={form.linkUrl ?? ""}
                    onChange={(e) => patch({ linkUrl: e.target.value.trim() || null })}
                    placeholder="https://status.maxlayer.cloud"
                  />
                </Field>

                {form.linkUrl && (
                  <Field label="Link label" htmlFor="linkLabel" hint="Defaults to “Details”.">
                    <Input
                      id="linkLabel"
                      value={form.linkLabel ?? ""}
                      maxLength={40}
                      onChange={(e) => patch({ linkLabel: e.target.value.trim() || null })}
                      placeholder="Status page"
                    />
                  </Field>
                )}
              </div>
            </Card>

            <Card>
              <CardHeader title="Who sees it" />
              <div className="space-y-4 p-5 pt-0">
                <Field
                  label="Audience"
                  htmlFor="audience"
                  hint={AUDIENCES.find((a) => a.value === form.audience)?.hint}
                >
                  <Select
                    id="audience"
                    value={form.audience}
                    onChange={(e) => {
                      const audience = e.target.value as AnnouncementAudience;
                      // Clear the scope on the way out of dashboard-only rather
                      // than letting the API reject a pair the form allowed.
                      patch({
                        audience,
                        ...(audience === "dashboard" ? {} : { serverId: null }),
                      });
                    }}
                  >
                    {AUDIENCES.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </Select>
                </Field>

                {canScopeToServer ? (
                  <Field
                    label="Scope"
                    htmlFor="server"
                    hint="Platform-wide, or only the organizations with an app or database on one server."
                  >
                    <ServerPicker
                      id="server"
                      ariaLabel="Limit this notice to one server"
                      value={form.serverId ?? ""}
                      initialLabel={existing.data?.serverName ?? null}
                      emptyLabel="Everyone — platform-wide"
                      onChange={(serverId) => patch({ serverId: serverId || null })}
                    />
                  </Field>
                ) : (
                  <p className="text-sm text-fg-subtle">
                    Server scoping is available on <strong className="text-fg-muted">Dashboard
                    only</strong>. The public site has no way to tell which server a visitor's apps
                    are on, so a scoped notice there would show one server's outage as the
                    platform's.
                  </p>
                )}
              </div>
            </Card>

            <Card>
              <CardHeader title="When it shows" />
              <div className="space-y-4 p-5 pt-0">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Starts"
                    htmlFor="startsAt"
                    hint="Blank starts as soon as you publish."
                  >
                    <Input
                      id="startsAt"
                      type="datetime-local"
                      value={toLocalInput(form.startsAt)}
                      onChange={(e) =>
                        patch({ startsAt: fromLocalInput(e.target.value) ?? undefined })
                      }
                    />
                  </Field>

                  <Field label="Ends" htmlFor="endsAt" hint="Blank runs until you retract it.">
                    <Input
                      id="endsAt"
                      type="datetime-local"
                      value={toLocalInput(form.endsAt)}
                      onChange={(e) => patch({ endsAt: fromLocalInput(e.target.value) })}
                    />
                  </Field>
                </div>

                {/* The usual case in one click. Typing two datetime-locals to
                    express "an hour from now" is the kind of friction that ends
                    with somebody publishing a window they did not mean. */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="micro">Window</span>
                  {DURATIONS.map((d) => (
                    <button
                      key={d.label}
                      type="button"
                      onClick={() => setDuration(d.minutes)}
                      className="rounded-md border border-line px-2 py-1 text-xs text-fg-muted transition-colors duration-200 ease-smooth hover:border-line-strong hover:bg-raised hover:text-fg"
                    >
                      {d.label}
                    </button>
                  ))}
                  {form.endsAt && (
                    <button
                      type="button"
                      onClick={() => patch({ endsAt: null })}
                      className="rounded-md px-2 py-1 text-xs text-fg-subtle transition-colors duration-200 ease-smooth hover:bg-raised hover:text-fg-muted"
                    >
                      Clear end
                    </button>
                  )}
                </div>
              </div>
            </Card>

            <Card>
              <CardHeader title="How it behaves" />
              <div className="space-y-4 p-5 pt-0">
                <Check
                  checked={form.dismissible}
                  onChange={(v) => patch({ dismissible: v })}
                  label="Readers can close it"
                  detail="Turn this off for an active incident. Somebody who dismissed “everything is down” and then forgot has been misled by the product."
                />
                <Check
                  checked={form.published}
                  onChange={(v) => patch({ published: v })}
                  label="Published"
                  detail="Live within a minute on every surface in the audience above. Retracting takes the same minute."
                />
              </div>
            </Card>
          </div>

        </div>

        {/* Pinned, so the save control is reachable without scrolling back up
            from the bottom of four cards. */}
        <div className="sticky bottom-0 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line bg-canvas/95 py-3 backdrop-blur">
          <p className="text-xs text-fg-subtle">
            {dirty ? "Unsaved changes" : editing ? "No changes yet" : "Nothing saved yet"}
          </p>
          <div className="flex gap-2">
            <Link to="/announcements" className={buttonStyles("secondary", "md")}>
              Cancel
            </Link>
            <Button
              type="submit"
              loading={saving}
              disabled={form.title.trim() === "" || form.body.trim() === ""}
            >
              {editing ? "Save changes" : "Create announcement"}
            </Button>
          </div>
        </div>
      </form>

      <Modal
        open={blocker.state === "blocked"}
        onClose={() => blocker.reset?.()}
        title="Leave without saving?"
        description="What you typed will be lost. Nothing has been sent to the API."
      >
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => blocker.reset?.()}>
            Keep editing
          </Button>
          <Button variant="danger" onClick={() => blocker.proceed?.()}>
            Discard changes
          </Button>
        </div>
      </Modal>
    </div>
  );
}

/**
 * How much of the field is used, and a warning as it runs out.
 *
 * The limits are real — the API rejects past them — and they exist because this
 * renders into a bar rather than a page. A counter that only appears when it
 * matters would be a counter nobody learns to look for, so it is always there
 * and only changes colour.
 */
function Counter({ used, max }: { used: number; max: number }) {
  const share = used / max;
  return (
    <span
      className={cx(
        "text-xs tabular-nums",
        share >= 1 ? "text-danger" : share > 0.85 ? "text-warn" : "text-fg-subtle",
      )}
    >
      {used}/{max}
    </span>
  );
}

function Check({
  checked,
  onChange,
  label,
  detail,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  detail: string;
}) {
  return (
    <label className="flex items-start gap-2.5 text-sm text-fg">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-4 shrink-0 accent-accent"
      />
      <span>
        {label}
        <span className="mt-0.5 block text-xs leading-5 text-fg-subtle">{detail}</span>
      </span>
    </label>
  );
}

/*
 * The preview's own tone map, and the third copy of it in this workspace.
 *
 * Duplicated deliberately, the same trade the error envelope makes: there is no
 * package between these repositories and adding one would couple three
 * independent deploys, so the cost is twenty lines and the benefit is that an
 * operator can see what they are about to publish.
 *
 * The colours are *this console's* tokens, not the customer dashboard's, and that
 * is a rule rather than an oversight — the accent is the only thing left
 * distinguishing the two consoles at a glance, and copying the customer blue in
 * here to make the preview exact is explicitly not allowed. `warn` and `danger`
 * mean the same thing in both, so maintenance and incident are faithful; info and
 * promo wear amber here and blue there. The panel's own description says so,
 * because the preview's job is length and wording rather than hue.
 */
const PREVIEW_TONES: Record<
  AnnouncementKind,
  { surface: string; mark: string; label: string; pulse?: boolean; solid?: boolean }
> = {
  info: { surface: "border-accent/25 bg-accent/[0.07]", mark: "text-accent", label: "Announcement" },
  maintenance: { surface: "border-warn/25 bg-warn/[0.07]", mark: "text-warn", label: "Maintenance" },
  incident: {
    surface: "border-danger/25 bg-danger/[0.07]",
    mark: "text-danger",
    label: "Incident",
    pulse: true,
  },
  promo: {
    surface: "border-accent bg-accent text-on-inverse",
    mark: "text-on-inverse",
    label: "Offer",
    solid: true,
  },
};

/**
 * The bar as the customer meets it, at two widths.
 *
 * Both, because the failure this exists to catch is width-dependent: a body that
 * sits on one line at 1280px is three lines and a clamp at 390px, and the
 * narrow case is the one nobody checks. The mobile column is a real 390px rather
 * than a scaled-down copy, so the wrapping shown is the wrapping that happens.
 */
function Preview({ form, width }: { form: AnnouncementInput; width: "desktop" | "mobile" }) {
  const tone = PREVIEW_TONES[form.kind];
  const mobile = width === "mobile";

  const title = form.title.trim() || "Untitled notice";
  const body = form.body.trim() || "The body goes here.";
  /*
   * The real phrase, not a placeholder. It was "ends in …", which showed that
   * *something* would appear there and not what — and the length of that
   * something is exactly what a preview is for: "ends in 47 minutes" is nearly
   * three times the width of "ends in 2h".
   */
  const timing = previewTiming(form);

  return (
    <div
      className={cx(
        "overflow-hidden rounded-md border border-line bg-canvas",
        mobile && "mx-auto w-[390px] max-w-full",
      )}
    >
      <div className={cx("border-b", tone.surface)}>
        <div
          className={cx(
            "flex gap-1.5 px-3 py-2",
            mobile ? "flex-col" : "flex-row items-baseline gap-3",
          )}
        >
          <span className="flex shrink-0 items-center gap-2">
            <span
              className={cx(
                "dot-status translate-y-px",
                tone.solid ? "text-current" : tone.mark,
                tone.pulse && "dot-status-pulse",
              )}
              aria-hidden="true"
            />
            <span className={cx("micro", tone.solid ? "text-current" : tone.mark)}>
              {tone.label}
            </span>
          </span>

          <p className="min-w-0 flex-1 text-[13px] leading-6 line-clamp-2">
            <span className={cx("font-medium", tone.solid ? "text-current" : "text-fg")}>
              {title}
            </span>
            <span className={tone.solid ? "text-current/85" : "text-fg-muted"}> — {body}</span>
          </p>

          <span className={cx("flex shrink-0 items-center gap-3", mobile && "w-full")}>
            {timing && (
              <span
                className={cx(
                  "text-[11px] tabular-nums whitespace-nowrap",
                  tone.solid ? "text-current/75" : "text-fg-subtle",
                )}
              >
                {timing}
              </span>
            )}
            {form.linkUrl && (
              <span
                className={cx(
                  "inline-flex items-center gap-1 text-[13px] font-medium whitespace-nowrap",
                  tone.solid ? "text-current underline underline-offset-2" : "text-accent",
                )}
              >
                {form.linkLabel || "Details"}
                <span aria-hidden="true">→</span>
              </span>
            )}
            {form.dismissible && (
              <span
                aria-hidden="true"
                className={cx(
                  "text-sm",
                  mobile && "ml-auto",
                  tone.solid ? "text-current/75" : "text-fg-subtle",
                )}
              >
                ✕
              </span>
            )}
          </span>
        </div>
      </div>

      {/* A sliver of page under the bar, so the preview reads as a bar on a page
          rather than as a card of its own. Without it the tint has no ground to
          sit against and every kind looks equally loud. */}
      <div className="h-8 bg-canvas" />
    </div>
  );
}

/**
 * The same sentence the two banners build, rebuilt here from the form.
 *
 * A fourth copy of a small rule, and worth it: previewing the *shape* of the bar
 * while faking the one part that changes width defeats the purpose. Which of the
 * two dates to show is the whole decision — before the window opens the
 * interesting fact is the start, once open it is the end.
 */
export function previewTiming(form: AnnouncementInput): string | null {
  const now = Date.now();
  const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

  const say = (ms: number) => {
    const minutes = Math.round(ms / 60_000);
    if (Math.abs(minutes) < 60) return relative.format(minutes, "minute");
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 24) return relative.format(hours, "hour");
    return relative.format(Math.round(hours / 24), "day");
  };

  if (form.startsAt) {
    const start = new Date(form.startsAt).getTime();
    if (Number.isFinite(start) && start > now) return `starts ${say(start - now)}`;
  }

  if (!form.endsAt) return null;
  const end = new Date(form.endsAt).getTime();
  if (!Number.isFinite(end)) return null;
  return end > now ? `ends ${say(end - now)}` : "ending now";
}

/**
 * `datetime-local` speaks the operator's wall clock and the API speaks UTC, so
 * these two convert at the edge.
 *
 * `toISOString().slice(0, 16)` is the obvious version and is wrong: it renders
 * the UTC time into a control the browser labels as local, so an operator in
 * UTC+7 sets a window and sees it seven hours off. Subtracting the offset first
 * is what makes the displayed value the one they typed.
 */
export function toLocalInput(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export { blankInput };
